import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Switch,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { post, get } from "../../../utils/http";
import {
  STATUS,
  ROLES,
  ROLE_MAP,
  STATUS_TAG_COLOR,
  STATUS_WORDS_MAP,
} from "../../../utils/consts";
import { DialogData } from "../types";
import AudioPlayButton from "./AudioPlayButton";

interface DialogTableProps {
  data: DialogData[];
  sceneId: string;
}

function DialogTable({ data: initialData, sceneId }: DialogTableProps) {
  const [dialogData, setDialogData] = useState<DialogData[]>(initialData);
  const [dialogModalVisible, setDialogModalVisible] = useState(false);
  const [dialogForm] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState<DialogData | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<DialogData | null>(null);
  const [generatingAudioId, setGeneratingAudioId] = useState<string | null>(
    null,
  );

  const fetchDialogData = async () => {
    try {
      const response = await get("/api/manage/scene-sentences", {
        scene_id: sceneId,
      });
      console.log("🚀 ~ fetchDialogData ~ response:", response);
      if (response?.data?.code === 0) {
        setDialogData(
          Array.isArray(response.data.data) ? response.data.data : [],
        );
      }
    } catch (error) {
      console.error("🚀 ~ fetchDialogData ~ error:", error);
    }
  };

  useEffect(() => {
    setDialogData(initialData);
  }, [initialData]);

  const dialogColumns: ColumnsType<DialogData> = [
    {
      title: "序号",
      dataIndex: "sentence_order",
      key: "sentence_order",
    },
    {
      title: "内容",
      dataIndex: "sentence_text",
      key: "sentence_text",
    },
    {
      title: "翻译",
      dataIndex: "translation",
      key: "translation",
    },
    {
      title: "音频",
      dataIndex: "voice",
      key: "voice",
      render: (voice, record) =>
        voice ? (
          <AudioPlayButton voiceUrl={voice} recordId={record.id} />
        ) : (
          "暂无音频"
        ),
    },
    {
      title: "所属角色",
      dataIndex: "role",
      key: "role",
      render: (role) => ROLE_MAP[role as ROLES],
    },
    {
      title: "是否启用",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checked={status === STATUS.INUSE}
            onChange={(checked) => {
              const newStatus = checked ? STATUS.INUSE : STATUS.DISABLE;
              post("/api/manage/sentence-update", {
                id: record.id,
                status: newStatus,
              })
                .then((res) => {
                  if (res?.data?.code === 0) {
                    message.success("状态更新成功");
                    fetchDialogData();
                  } else {
                    message.error("状态更新失败");
                  }
                })
                .catch((error) => {
                  console.error("状态更新失败:", error);
                  message.error("状态更新失败");
                });
            }}
          />
          <Tag color={STATUS_TAG_COLOR[status as STATUS]}>
            {STATUS_WORDS_MAP[status as STATUS]}
          </Tag>
        </span>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 320,
      render: (_, record, index) => (
        <span>
          <a style={{ marginRight: 16 }} onClick={() => handleEdit(record)}>
            编辑
          </a>
          <Button
            type="link"
            loading={generatingAudioId === record.id}
            disabled={generatingAudioId !== null}
            onClick={() => handleGenerateAudio(record)}
            style={{ marginRight: 16 }}
          >
            生成音频
          </Button>
          <a
            style={{
              marginRight: 16,
              color: index === 0 ? "#ccc" : undefined,
              cursor: index === 0 ? "not-allowed" : "pointer",
            }}
            onClick={() => index !== 0 && handleMoveUp(index)}
          >
            上移
          </a>
          <a
            style={{
              marginRight: 16,
              color: index === dialogData.length - 1 ? "#ccc" : undefined,
              cursor:
                index === dialogData.length - 1 ? "not-allowed" : "pointer",
            }}
            onClick={() =>
              index !== dialogData.length - 1 && handleMoveDown(index)
            }
          >
            下移
          </a>
          <a onClick={() => handleDelete(record)}>删除</a>
        </span>
      ),
    },
  ];

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newData = [...dialogData];
    const temp = newData[index];
    newData[index] = newData[index - 1];
    newData[index - 1] = temp;
    await updateBatchOrder(newData);
  };

  const handleMoveDown = async (index: number) => {
    if (index === dialogData.length - 1) return;
    const newData = [...dialogData];
    const temp = newData[index];
    newData[index] = newData[index + 1];
    newData[index + 1] = temp;
    await updateBatchOrder(newData);
  };

  const updateBatchOrder = async (newData: DialogData[]) => {
    const sentenceOrders = newData.map((item, index) => ({
      sentence_id: item.id,
      sentence_order: index + 1,
    }));

    try {
      const response = await post("/api/manage/sentence-batch-update-order", {
        scene_id: sceneId,
        sentence_orders: sentenceOrders,
      });
      if (response?.data?.code === 0) {
        message.success("顺序更新成功");
        fetchDialogData();
      } else {
        message.error("顺序更新失败");
      }
    } catch (error) {
      console.error("顺序更新失败:", error);
      message.error("顺序更新失败");
    }
  };

  const handleAddDialog = () => {
    setDialogModalVisible(true);
  };

  const handleDialogModalOk = async () => {
    try {
      const values = await dialogForm.validateFields();
      const apiUrl = editingRecord
        ? "/api/manage/sentence-update"
        : "/api/manage/sentence-add";
      const requestData = editingRecord
        ? {
            scene_id: sceneId,
            id: editingRecord.id,
            sentence_text: values.content,
            translation: values.translation,
            role: values.role,
            status: values.status,
          }
        : {
            scene_id: sceneId,
            sentence_text: values.content,
            translation: values.translation,
            role: values.role,
            status: values.status,
          };
      const response = await post(apiUrl, requestData);
      console.log("🚀 ~ handleDialogModalOk ~ response:", response);
      if (response?.data?.code === 0) {
        setDialogModalVisible(false);
        dialogForm.resetFields();
        setEditingRecord(null);
        fetchDialogData();
      }
    } catch (error) {
      console.error("🚀 ~ handleDialogModalOk ~ error:", error);
    }
  };

  const handleDialogModalCancel = () => {
    setDialogModalVisible(false);
    dialogForm.resetFields();
    setEditingRecord(null);
  };

  const handleEdit = (record: DialogData) => {
    setEditingRecord(record);
    dialogForm.setFieldsValue({
      content: record.sentence_text,
      translation: record.translation,
      role: record.role,
      status: record.status,
    });
    setDialogModalVisible(true);
  };

  const handleDelete = (record: DialogData) => {
    setDeletingRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    try {
      const response = await post("/api/manage/sentence-delete", {
        id: deletingRecord.id,
      });
      console.log("🚀 ~ handleDeleteConfirm ~ response:", response);
      if (response?.data?.code === 0) {
        setDeleteModalVisible(false);
        setDeletingRecord(null);
        fetchDialogData();
      }
    } catch (error) {
      console.error("🚀 ~ handleDeleteConfirm ~ error:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingRecord(null);
  };

  const handleGenerateAudio = async (record: DialogData) => {
    if (generatingAudioId !== null) {
      return;
    }

    setGeneratingAudioId(record.id);

    try {
      const response = await post("/api/manage/text-to-audio-and-save", {
        text: record.sentence_text,
        table_type: "sentence",
        record_id: record.id,
        roles: record.role,
      });
      console.log("🚀 ~ handleGenerateAudio ~ response:", response);
      if (response?.data?.code === 0) {
        message.success("音频生成成功");
        fetchDialogData();
      }
    } catch (error) {
      console.error("🚀 ~ handleGenerateAudio ~ error:", error);
      message.error("音频生成失败");
    } finally {
      setGeneratingAudioId(null);
    }
  };

  return (
    <>
      <Table
        columns={dialogColumns}
        dataSource={dialogData}
        rowKey="id"
        pagination={false}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={handleAddDialog}
                >
                  添加对话
                </Button>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Modal
        title={editingRecord ? "编辑对话" : "添加对话"}
        open={dialogModalVisible}
        onOk={handleDialogModalOk}
        onCancel={handleDialogModalCancel}
        width={600}
      >
        <Form form={dialogForm} layout="vertical">
          <Form.Item
            label="对话内容"
            name="content"
            rules={[{ required: true, message: "请输入对话内容" }]}
          >
            <Input.TextArea rows={4} placeholder="请输入对话内容" />
          </Form.Item>
          <Form.Item
            label="翻译"
            name="translation"
            rules={[{ required: true, message: "请输入翻译" }]}
          >
            <Input.TextArea rows={4} placeholder="请输入翻译" />
          </Form.Item>
          <Form.Item
            label="所属角色"
            name="role"
            rules={[{ required: true, message: "请选择所属角色" }]}
          >
            <Select placeholder="请选择所属角色">
              <Select.Option value={ROLES.SPEAKER_A}>
                {ROLE_MAP[ROLES.SPEAKER_A]}
              </Select.Option>
              <Select.Option value={ROLES.SPEAKER_B}>
                {ROLE_MAP[ROLES.SPEAKER_B]}
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="是否启用"
            name="status"
            rules={[{ required: true, message: "请选择是否启用" }]}
          >
            <Select placeholder="请选择是否启用">
              <Select.Option value={STATUS.INUSE}>
                {STATUS_WORDS_MAP[STATUS.INUSE]}
              </Select.Option>
              <Select.Option value={STATUS.DISABLE}>
                {STATUS_WORDS_MAP[STATUS.DISABLE]}
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="确认"
        cancelText="取消"
      >
        <p>确定要删除这条对话吗？</p>
      </Modal>
    </>
  );
}

export default DialogTable;
